// Gateway client for the speaking game (/api/v1/learn/*). Identity is the
// same as the warm-up's: the web session JWT, else an anonymous device token.
import { BABA_GATEWAY_BASE_URL } from '../gateway'

/** Worker root — the gateway base minus its `/api/v1` suffix. Images and other
 *  worker-served assets hang off this, and it must come from the client's own
 *  configuration, never from a URL the server built. */
export const LEARN_WORKER_ORIGIN = BABA_GATEWAY_BASE_URL.replace(/\/api\/v1\/?$/, '')
import { forgetDrillDeviceToken, resolveDrillIdentity } from '../drill/identity'
import type { AttemptResponse, Band, DrillGender, DrillPace, PaywallBody, ProgressResponse, PromptKind, RoundCompleteResponse, RoundResponse } from './types'

export class LearnApiError extends Error {
  readonly status: number
  readonly code: string | null
  readonly paywall: PaywallBody['paywall'] | null
  constructor(message: string, status: number, code: string | null = null, paywall: PaywallBody['paywall'] | null = null) {
    super(message)
    this.name = 'LearnApiError'
    this.status = status
    this.code = code
    this.paywall = paywall
  }
}

async function parseError(res: Response): Promise<LearnApiError> {
  let message = `Request failed (${res.status})`
  let code: string | null = null
  let paywall: PaywallBody['paywall'] | null = null
  try {
    const body = (await res.json()) as Partial<PaywallBody> & { error?: { message?: string; code?: string | null } }
    if (body?.error?.message) message = body.error.message
    if (body?.error?.code !== undefined) code = body.error.code ?? null
    if (body?.paywall) paywall = body.paywall
  } catch {
    // not JSON
  }
  return new LearnApiError(message, res.status, code, paywall)
}

async function authedFetch(path: string, init: RequestInit = {}, retryOn401 = true): Promise<Response> {
  const identity = await resolveDrillIdentity()
  const headers = new Headers(init.headers)
  headers.set('Authorization', `Bearer ${identity.token}`)
  const res = await fetch(`${BABA_GATEWAY_BASE_URL}${path}`, { ...init, headers })
  if (res.status === 401 && identity.kind === 'device' && retryOn401) {
    forgetDrillDeviceToken()
    return authedFetch(path, init, false)
  }
  return res
}

function tz(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

export async function fetchProgress(): Promise<ProgressResponse> {
  const res = await authedFetch(`/learn/progress?tz=${encodeURIComponent(tz())}`)
  if (!res.ok) throw await parseError(res)
  return (await res.json()) as ProgressResponse
}

export interface StartRoundParams {
  band: Band
  gender: DrillGender
  pace: DrillPace
  kind?: PromptKind
  lexemeId?: string
}

export async function startRound(p: StartRoundParams): Promise<RoundResponse> {
  const res = await authedFetch('/learn/round', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...p, tz: tz(), platform: 'web' }),
  })
  if (!res.ok) throw await parseError(res)
  return (await res.json()) as RoundResponse
}

/** Fetch an auth-gated clip (English prompts, canned lines) and return an object URL the caller revokes. */
export async function fetchClipUrl(path: string): Promise<string> {
  const res = await authedFetch(path)
  if (!res.ok) throw await parseError(res)
  return URL.createObjectURL(await res.blob())
}

export interface SubmitAttemptParams {
  roundId: string
  itemToken: string
  attemptId: string
  attemptIndex: number
  stepIndex?: number
  gender: DrillGender
  clip: Blob
  clientVerdict?: 'correct'
  clientPartial?: string
}

export async function submitAttempt(p: SubmitAttemptParams): Promise<AttemptResponse> {
  const form = new FormData()
  form.set('roundId', p.roundId)
  form.set('itemToken', p.itemToken)
  form.set('attemptId', p.attemptId)
  form.set('attemptIndex', String(p.attemptIndex))
  form.set('stepIndex', String(p.stepIndex ?? 0))
  form.set('gender', p.gender)
  if (p.clientVerdict) form.set('clientVerdict', p.clientVerdict)
  if (p.clientPartial) form.set('clientPartial', p.clientPartial.slice(0, 200))
  const ext = p.clip.type.includes('mp4') ? 'mp4' : p.clip.type.includes('ogg') ? 'ogg' : 'webm'
  form.set('audio', p.clip, `clip.${ext}`)
  const res = await authedFetch('/learn/attempt', { method: 'POST', body: form })
  if (!res.ok) throw await parseError(res)
  return (await res.json()) as AttemptResponse
}

export interface ListenTimeoutParams {
  roundId: string
  itemToken: string
  attemptIndex: number
  stepIndex?: number
  gender: DrillGender
  peakLevel: number
  noiseFloor: number
}

export async function reportListenTimeout(p: ListenTimeoutParams): Promise<void> {
  try {
    const form = new FormData()
    form.set('event', 'listen_timeout')
    form.set('roundId', p.roundId)
    form.set('itemToken', p.itemToken)
    form.set('attemptId', crypto.randomUUID())
    form.set('attemptIndex', String(p.attemptIndex))
    form.set('stepIndex', String(p.stepIndex ?? 0))
    form.set('gender', p.gender)
    form.set('peakLevel', p.peakLevel.toFixed(4))
    form.set('noiseFloor', p.noiseFloor.toFixed(4))
    await authedFetch('/learn/attempt', { method: 'POST', body: form })
  } catch {
    // telemetry only
  }
}

export interface RoundRecap {
  itemCount: number
  correctCount: number
  firstTryCount: number
  assistedCount: number
  durationMs: number
  items: { itemId: string; outcome: 'correct' | 'assisted' | 'pending'; attempts: number; correctOn: number | null }[]
  sounds: { sound: string; attempted: number; clean: number }[]
}

export async function completeRound(roundId: string, recap: RoundRecap): Promise<RoundCompleteResponse | null> {
  try {
    const res = await authedFetch('/learn/round/complete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ roundId, recap, tz: tz() }),
    })
    if (!res.ok) return null
    return (await res.json()) as RoundCompleteResponse
  } catch {
    return null
  }
}

export interface HeardWordParams {
  /** One id per tutor call. It IS the ephemeral round id server-side, which is
   *  what makes the round insert idempotent without a read. */
  conversationId: string
  lexemeId: string
  /** The learner's own transcript, verbatim. The server re-judges it. */
  heard: string
}

export interface HeardWordResponse {
  verdict: 'counted' | 'ignored'
  graduated: boolean
  dueNext: string | null
}

/**
 * Credit a word the learner said out loud to Avi.
 *
 * Fire and forget by contract: it never throws and never returns an error for
 * the caller to render. A live conversation must not stop, stutter, or show a
 * red line because a bookkeeping POST failed — the learner did not ask for
 * this call and would have no idea what it referred to. `correct: true` is
 * only a hint; the server re-runs the judge over the corpus's own accepted
 * forms and answers `ignored` when it disagrees.
 */
export async function reportHeardWord(p: HeardWordParams): Promise<HeardWordResponse | null> {
  try {
    const res = await authedFetch('/learn/heard', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        conversationId: p.conversationId,
        attemptId: crypto.randomUUID(),
        lexemeId: p.lexemeId,
        heard: p.heard.slice(0, 200),
        correct: true,
        tz: tz(),
      }),
    })
    if (!res.ok) return null
    return (await res.json()) as HeardWordResponse
  } catch {
    return null
  }
}
